import React, { useMemo } from "react";

import { Grid } from "@material-ui/core";
import { getIn, useFormikContext } from "formik";

import { useSelector } from "react-redux";

import StateAwareBusinessPICSelector from "components/BusinessPICSelector";
import { BidderNumberInput } from "components/Form/BidderNumberInput";
import {
  BusinessFieldWithFilter,
  BuyerAwareBuyerWayField,
} from "components/Form/Fields";
import {
  CheckBox,
  ControlledLifeCycleInput,
  Input,
  IntegerPriceInput,
  parseDecimalInputValueDefaultToNull,
  PercentageInputProps,
  withNamespace,
} from "components/Form/FormikControls";
import { BuyerSplit } from "components/SellingSaleLotForm/BuyerSplit";

import { BUYER, TRANSPORTER } from "constants/businesses";
import { SaleyardPermissions } from "constants/permissions";
import { SaleType } from "constants/sale";

import {
  ForClearingSale,
  ForLivestockAuction,
  ForLivestockAuctionAndPaddock,
  ForPaddock,
  ForSaleType,
} from "containers/ForSaleType";

import { formatPercentage, getDollarPriceStringFromCents } from "lib";

import {
  getCurrentSale,
  getCurrentSaleyardId,
  selectCurrentDeploymentSales,
} from "selectors";

import {
  useGetBuyerWayFromBidder,
  useHasBuyerSplitPermission,
  useHasSaleyardPermission,
} from "hooks";

import { PricingType } from "./PricingType";

const businessRoles = [BUYER];

interface SellingSaleLotFormProps {
  deploymentSaleId: number;
  namespace: string;
  quantity: number;
  readOnly: boolean;
}
interface FormValues {
  buyer_id: string;
}

export function SellingSaleLotForm(props: SellingSaleLotFormProps) {
  const { deploymentSaleId, namespace: ns, quantity, readOnly } = props;

  const saleyardId = useSelector(getCurrentSaleyardId);
  const isUsingRegisteredBidders =
    useSelector(getCurrentSale)?.using_registered_bidders;

  const formikProps = useFormikContext<FormValues>();
  const getBuyerWayFromBidder = useGetBuyerWayFromBidder();

  const { values, setFieldValue } = formikProps;

  const buyerWayName = getIn(values, withNamespace(ns, "buyer_way.name"));

  const defaultListingFeeCents = useSelector(
    state =>
      selectCurrentDeploymentSales(state)[deploymentSaleId]
        ?.default_listing_fee_cents,
  );

  const hasBuyerSplitPermission = useHasBuyerSplitPermission();

  const defaultBuyersPremium =
    useSelector(
      state =>
        selectCurrentDeploymentSales(state)[deploymentSaleId]
          ?.default_buyers_premium,
    ) || 0;

  function clearBuyerWay(businessId) {
    if (businessId !== getIn(values, withNamespace(ns, "buyer_id"))) {
      setFieldValue(withNamespace(ns, "buyer_way"), null);
    }
  }

  function onExistingBidderChanged(newBidder) {
    if (values.buyer_id !== String(newBidder.businessId)) {
      const buyerWay = getBuyerWayFromBidder(newBidder);

      setFieldValue(withNamespace(ns, "buyer_id"), newBidder.businessId);

      setFieldValue(
        withNamespace(ns, "buyer_way"),
        buyerWay.id ? buyerWay : null,
      );
      setFieldValue(
        withNamespace(ns, "destination_property_id"),
        newBidder.defaultPropertyId,
      );
    }
  }

  const saleType = useSelector(getCurrentSale)?.sale_type;

  const hasTransporterPermission = useHasSaleyardPermission(
    SaleyardPermissions.featureTransporters,
  );

  const buyerWayColMd = useMemo(() => {
    if (saleType === SaleType.PADDOCK) {
      return 12;
    }
    if (isUsingRegisteredBidders) {
      return 4;
    }
    return 6;
  }, [isUsingRegisteredBidders, saleType]);

  return (
    <>
      <ForClearingSale>
        <Grid item xs={12} md={6}>
          <IntegerPriceInput
            beforeSymbol="$"
            decimalPlaces={2}
            label="Listing Fee"
            name="listingFee"
            placeholder={getDollarPriceStringFromCents(defaultListingFeeCents)}
            tooltip={`The Listing Fee for this Lot. If left blank, this Lot will use the default for the Sale ($${getDollarPriceStringFromCents(
              defaultListingFeeCents,
            )}).`}
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <IntegerPriceInput
            label="Reserve Price"
            name="reservePrice"
            disabled={readOnly}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <IntegerPriceInput
            label="Starting Bid"
            name="startingBid"
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <IntegerPriceInput
            label="Bid Increment"
            name="bidIncrement"
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <IntegerPriceInput
            label="Original Listing Price"
            name="originalListingPrice"
            disabled={readOnly}
          />
        </Grid>
      </ForClearingSale>
      <Grid xs={12} item>
        <StateAwareBusinessPICSelector
          buyerWayName={buyerWayName}
          label="Buyer"
          businessFieldName="buyer_id"
          propertyFieldName="destination_property_id"
          formikProps={formikProps}
          businessRoles={businessRoles}
          onAfterSelect={clearBuyerWay}
          disabled={readOnly}
        />
      </Grid>
      <ForLivestockAuctionAndPaddock>
        <Grid item xs={12} md={buyerWayColMd}>
          <BuyerAwareBuyerWayField
            name={withNamespace(ns, "buyer_way")}
            label="Buyer Way"
            buyerField={withNamespace(ns, "buyer_id")}
            readOnly={readOnly}
            saleyardId={saleyardId}
          />
        </Grid>
        {isUsingRegisteredBidders && (
          <Grid item xs={12} md={4}>
            <BidderNumberInput
              disabled={readOnly}
              onExistingBidderChanged={onExistingBidderChanged}
            />
          </Grid>
        )}
      </ForLivestockAuctionAndPaddock>
      <ForLivestockAuction>
        <Grid item xs={12} md={isUsingRegisteredBidders ? 4 : 6}>
          <StateAwareBusinessPICSelector
            label="Third Party"
            tooltip="Commission Buyer"
            businessFieldName={withNamespace(ns, "thirdPartyId")}
            formikProps={formikProps}
            businessRoles={businessRoles}
            optional
            clearable
            disabled={readOnly}
          />
        </Grid>
      </ForLivestockAuction>
      {hasTransporterPermission && (
        <Grid xs={12} md={12} item>
          <BusinessFieldWithFilter
            name="transporterId"
            label="Transporter"
            defaultBusinessFilter={[TRANSPORTER]}
            globalBusinesses
            hideListboxComponent
            disabled={readOnly}
            allowAddNew={false}
            hideFilteredCount
          />
        </Grid>
      )}
      <ForClearingSale>
        <Grid item xs={12} md={6}>
          {isUsingRegisteredBidders ? (
            <BidderNumberInput
              disabled={readOnly}
              onExistingBidderChanged={onExistingBidderChanged}
            />
          ) : (
            <Input
              label="Buyer Number"
              name="buyerNumber"
              type="number"
              disabled={readOnly}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <ControlledLifeCycleInput
            {...PercentageInputProps}
            disabled={readOnly}
            label="Buyers Premium"
            name="buyersPremium"
            parseInputValue={parseDecimalInputValueDefaultToNull}
            placeholder={formatPercentage(defaultBuyersPremium, {
              includeSymbol: false,
              roundingMethod: null,
            })}
            tooltip={`The Buyer's Premium for this Lot. If left blank, this Lot will use the default for the Sale (${formatPercentage(
              defaultBuyersPremium,
              { roundingMethod: null },
            )})`}
          />
        </Grid>
      </ForClearingSale>
      <Grid item xs={12}>
        <PricingType namespace={ns} quantity={quantity} readOnly={readOnly} />
      </Grid>
      <ForPaddock>
        <Grid item xs={12}>
          <IntegerPriceInput
            label="Reserve Price"
            name="reservePrice"
            disabled={readOnly}
          />
        </Grid>
      </ForPaddock>

      <Grid item xs={12} md={12}>
        <StateAwareBusinessPICSelector
          label="Invoice To"
          businessFieldName={withNamespace(ns, "invoiceToBusinessId")}
          formikProps={formikProps}
          businessRoles={businessRoles}
          optional
          clearable
          tooltip={
            hasBuyerSplitPermission
              ? "The business that receives the invoice for this Sale Lot.  This will" +
                " be overridden by any Buyer Splits."
              : ""
          }
        />
      </Grid>
      {hasBuyerSplitPermission && (
        <Grid item xs={12} container>
          <BuyerSplit />
        </Grid>
      )}

      <Grid item xs={6} md={6} container justifyContent="flex-start">
        <CheckBox label="GST Exempt" name="isGSTExempt" />
      </Grid>
      <ForSaleType saleType={[SaleType.EXTERNAL_AGENT_SALE]}>
        <Grid item xs={6} md={6} container justifyContent="center">
          <Input
            label="Account Note"
            name={withNamespace(ns, "accountNote")}
            disabled={readOnly}
          />
        </Grid>
      </ForSaleType>
    </>
  );
}
